import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, createBrowserRouter, createMemoryRouter, RouterProvider } from 'react-router-dom'
import { App, Frame } from './App'
import { httpApi } from './api/client'
import type { Api } from './api/types'
import { webSocketFeed } from './api/feed'
import { choosePasskeys } from './api/shellPasskeys'
import { terminalClientFactory } from './api/terminal'
import { webSocketTranscript } from './api/transcript'
import { toast } from './components/toasts'
import { listenForErrors, reporter, requestReport } from './lib/errorReports'
import { watchTheme } from './lib/theme'
import { inPhoneShell } from './lib/shell'
import { listenForNativePush, phoneLayoutNow } from './lib/nativePush'
import { nativeSessionOptions } from './mac/session'
import { nativeEmailLinkOptions } from './mac/emailLinks'
import { OpenAppPage } from './people/OpenApp'
import { mobilePrototype } from './mobile/entry'
import { BoardStore, type BoardStoreOptions } from './store/store'
import { StoreProvider } from './store/context'
import { SessionsProvider, type SessionsEnv } from './sessions/context'
import { Workspace } from './workspace/Workspace'
import { WorkspaceModel } from './workspace/model'
import { bootstrap, inWorkspace, nativeBootstrap, recoveryBootstrap } from './workspace/protocol'
import './styles/tokens.css'
import './styles/app.css'
import './styles/header.css'
import './styles/markdown.css'
import './styles/markdown-rich.css'
import './styles/sessions.css'
import './styles/transcript.css'
import './styles/code.css'
import './styles/working.css'
import './styles/queue.css'
import './styles/settings.css'
import './styles/search.css'
// Last: the Mac app's window chrome, active only under html[data-shell="mac"].
import './styles/shell.css'

// Set the theme before connecting or rendering. The Mac bundle permits only
// hashed module scripts, so index.html cannot carry a separate theme script.
const stopTheme = watchTheme()
if (import.meta.hot) import.meta.hot.dispose(stopTheme)

/**
 * The server named by VITE_POULPE_SERVER at `npm run dev` (vite.config.ts
 * passes /v1 on to it), signed in by the session's cookie. Or, in the dev
 * token mode, by a bearer token: the one the dev server adds to what it passes
 * on (POULPE_TOKEN_FILE, as `dev/demo` sets it), or else the one kept in this
 * browser under `poulpe.token`. The board follows the live feed's socket,
 * which the cookie or the dev server's token signs in; a WebSocket cannot
 * carry the token kept in the browser, so with that one alone it polls.
 * A session's transcript follows its own socket, signed in as the feed's, or
 * polls where the feed does.
 * Inside the Mac app, passkeys are the system's own, through its shell.
 * Without a server, the in-memory mock, its sample issues and a fake
 * authenticator, signed in as its person, polled, with an agent that plays a
 * short turn at each prompt, and its mail on the console.
 * With a server, the page's uncaught errors and its failed requests go to the
 * server's log, rate-limited (Poulpe-706).
 */
async function connect(): Promise<Pick<BoardStoreOptions, 'api' | 'passkeys' | 'byToken' | 'feed'> & Pick<SessionsEnv, 'openTranscript' | 'terminals'>> {
  if (import.meta.env.VITE_POULPE_SERVER) {
    const token = () => localStorage.getItem('poulpe.token')
    const kept = token() !== null
    const live = __POULPE_DEV_TOKEN__ || !kept
    // The reporter sends through the API it reports on: `api` is set before any failure.
    const report = reporter({ send: (r) => api.reportError(r) })
    const api: Api = httpApi({ baseUrl: '', token, onFailure: (failure) => report(requestReport(failure)) })
    listenForErrors(window, report)
    return {
      api,
      passkeys: choosePasskeys(),
      byToken: __POULPE_DEV_TOKEN__ || kept,
      feed: live ? webSocketFeed() : null,
      openTranscript: live ? webSocketTranscript() : null,
      ...(!__POULPE_DEV_TOKEN__ && !kept ? {terminals: terminalClientFactory()} : {}),
    }
  }
  const [{ mockApi }, { mockPasskeys }, { sampleData }] = await Promise.all([import('./api/mock'), import('./api/mockPasskeys'), import('./api/sample')])
  // The mock's mail goes to the console: its link opens here as the Mac app hands one over, by the event it names.
  const onMail = ({ to, link, attempt, token }: { to: string; link: string; attempt: string; token: string }) =>
    console.info(`The mock mailed ${to} ${link}\nOpen it here: dispatchEvent(new CustomEvent('poulpe-email-link', { detail: ${JSON.stringify({ attempt, token })} }))`)
  return { api: mockApi({ ...sampleData(), latencyMs: 150, scriptMs: 700, onMail }), passkeys: mockPasskeys(), openTranscript: null }
}

/**
 * The session's cookie is every tab's. Each tab tells the others when it
 * signs in or out, and reads who is signed in again when it comes to the
 * front. A token signs in no session.
 */
function followSession(store: BoardStore) {
  const refresh = () => void store.refreshSession()
  window.addEventListener('focus', refresh)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refresh()
  })
}

async function start() {
  // A sign-in mail's button for the Mac app: a page of its own, which hands
  // the link on and neither connects nor signs anyone in.
  if (window.location.pathname === '/open') {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <OpenAppPage hash={window.location.hash} go={(link) => window.location.assign(link)} />
      </StrictMode>,
    )
    return
  }
  // The phone's prototype, for review (mobile/README.md): the mock mode alone.
  // A live build sets the server, so this branch and its chunk fold away.
  if (!import.meta.env.VITE_POULPE_SERVER) {
    const prototype = mobilePrototype(window.location.pathname, import.meta.env.VITE_POULPE_SERVER)
    if (prototype) {
      const { MobilePrototype } = await prototype
      createRoot(document.getElementById('root')!).render(
        <StrictMode>
          <BrowserRouter><MobilePrototype /></BrowserRouter>
        </StrictMode>,
      )
      return
    }
  }
  const { openTranscript, terminals, ...connection } = await connect()
  const sessions = connection.byToken || typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('poulpe.session')
  // The Mac app's shell hands an email link over to the window as an event.
  const store = new BoardStore({ ...connection, ...nativeSessionOptions(), ...nativeEmailLinkOptions(), sessions, emailLinks: window, onFailure: (message) => toast(message, 'error') })
  const agentSessions: SessionsEnv = { api: connection.api, openTranscript, terminals, pollMs: 2_000 }
  if (!connection.byToken) followSession(store)
  // In the Mac app's window, the workspace: a router for each tab, from the tabs the shell kept.
  // Data routers, so that unsaved text can hold a navigation (useBlocker).
  const seed = nativeBootstrap() ?? document.documentElement.getAttribute('data-workspace-bootstrap')
  const workspace = inWorkspace()
    ? new WorkspaceModel(store, (path) => createMemoryRouter([{ path: '*', element: <Frame workspace /> }], { initialEntries: [path] }), bootstrap(seed), window.location.pathname + window.location.search + window.location.hash, recoveryBootstrap(seed))
    : null
  // Workspace tabs are sibling routers. An outer router would make every
  // pane's router nested and fail before any workspace could render.
  const router = workspace ? null : createBrowserRouter([{ path: '*', element: <App /> }])
  // The iPhone app hands its APNs token and a notice's tap to the page (Poulpe-627).
  if (router && inPhoneShell()) listenForNativePush(window, { notifications: () => store.phoneNotifications(), navigate: (path) => void router.navigate(path), phoneLayout: phoneLayoutNow })
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <StoreProvider store={store}>
        <SessionsProvider env={agentSessions}>
          {workspace ? <App workspace={<Workspace model={workspace} />} clearWorkspaceRecovery={() => workspace.clearRecovery()} /> : <RouterProvider router={router!} />}
        </SessionsProvider>
      </StoreProvider>
    </StrictMode>,
  )
  await store.start()
}

void start()
